
import React, { useEffect, useState, useMemo } from 'react';
import {
    Table, Input, Button, Modal, Space, message, Tag, Typography,
    Select, Spin, Tooltip, Empty, Tabs
} from "antd";
import {
    ReloadOutlined, SearchOutlined, EyeOutlined,
    FilePdfOutlined, FileTextOutlined,
    ClockCircleOutlined, CheckCircleOutlined, ShopOutlined, EditOutlined,
    SyncOutlined, ExclamationCircleOutlined, DollarOutlined,
    CalculatorOutlined,
} from "@ant-design/icons";
import { downloadDevisTransit, getAllCategoriesTransit, getDevisTransit, getMMarchandises, toggleDevisTransit } from '../../api/provision';

const { Text, Title } = Typography;
const { Option } = Select;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
    processing: { color: "processing", icon: <SyncOutlined spin />, hex: "#1677ff" },
    warning: { color: "warning", icon: <ClockCircleOutlined />, hex: "#faad14" },
    success: { color: "success", icon: <CheckCircleOutlined />, hex: "#52c41a" },
    error: { color: "error", icon: <ExclamationCircleOutlined />, hex: "#ff4d4f" },
};

const STATUT_CONFIG = {
    attente_masoandro: { ...STATUS_STYLES.processing, label: "En attente Masoandro" },
    valide_direction:  { ...STATUS_STYLES.success,    label: "Validé par la direction" },
    invalide_direction:{ ...STATUS_STYLES.error,      label: "Refusé par la direction" },
    attente_client:    { ...STATUS_STYLES.warning,    label: "En attente client" },
    valide_client:     { ...STATUS_STYLES.success,    label: "Validé par le client" },
    invalide_client:   { ...STATUS_STYLES.error,      label: "Refusé par le client" },
};

const StatutBadge = ({ statut }) => {
    const cfg = STATUT_CONFIG[statut] || { label: statut, hex: "#6b7280", bg: "#f3f4f6", icon: null };
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "2px 8px", borderRadius: 20, fontSize: 8, fontWeight: 600,
            color: cfg.hex, background: cfg.bg || `${cfg.hex}15`,
            border: `1px solid ${cfg.hex}30`, whiteSpace: "nowrap",
        }}>
            {cfg.icon && React.cloneElement(cfg.icon, { style: { color: cfg.hex, fontSize: 8 } })}
            {cfg.label}
        </span>
    );
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

const formatMontant = (montant) =>
    montant != null ? `${Number(montant).toLocaleString("fr-MG")} Ar` : "—";

const parseDevisInfos = (infosStr) => {
    try { return typeof infosStr === "string" ? JSON.parse(infosStr) : (infosStr || []); }
    catch { return []; }
};

const computeTotal = (infos) =>
    parseDevisInfos(infos).reduce((s, l) => s + (l.montant || 0), 0);

// ─── StatCard compacte ────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, bgColor }) => (
    <div style={{
        display: "flex", alignItems: "center", gap: 10,
        background: "#fff", borderRadius: 10, padding: "10px 14px",
        border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,.04)",
        height: "100%",
    }}>
        <div style={{
            width: 34, height: 34, borderRadius: 8, background: bgColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, color, flexShrink: 0,
        }}>
            {icon}
        </div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.2, whiteSpace: "nowrap" }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color, lineHeight: 1.3, whiteSpace: "nowrap" }}>{value}</div>
        </div>
    </div>
);

// ─── Modal visualisation devis ────────────────────────────────────────────────
const DevisViewModal = ({ open, onClose, devis, categories, marchandises }) => {
    console.log(devis);
    const lignes = parseDevisInfos(devis.devis_infos);
    const sections = useMemo(() => {
        const enriched = lignes.map(ligne => {
            const cat = categories.find(c => c.id === ligne.categorie_id);
            return { ...ligne, nom: cat?.nom || `Catégorie #${ligne.categorie_id}`, type: cat?.type || "Autre" };
        });
        return enriched.reduce((acc, ligne) => {
            if (!acc[ligne.type]) acc[ligne.type] = [];
            acc[ligne.type].push(ligne);
            return acc;
        }, {});
    }, [lignes, categories]);
    if (!devis) return null;
    const SECTION_COLORS = {
        "PRESTATION MASOANDRO": { color: "#185FA5", bg: "#eff6ff" },
        "A L'IDENTIQUE": { color: "#3B6D11", bg: "#f0fdf4" },
    };

    const tabItems = Object.entries(sections).map(([sectionName, sectionLignes]) => {
        const cfg = SECTION_COLORS[sectionName] || { color: "#6b7280", bg: "#f3f4f6" };
        return {
            key: sectionName,
            label: (
                <Space size={6}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color, display: "inline-block" }} />
                    {sectionName}
                    <Tag style={{ borderRadius: 10, fontSize: 11, border: "none", margin: 0, background: cfg.bg, color: cfg.color }}>
                        {sectionLignes.length}
                    </Tag>
                </Space>
            ),
            children: (
                <div>
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "8px 12px", borderRadius: 8, marginBottom: 12,
                        background: cfg.bg, border: `1px solid ${cfg.color}25`
                    }}>
                        <Text type="secondary" style={{ fontSize: 8 }}>
                            {sectionLignes.length} ligne{sectionLignes.length > 1 ? "s" : ""}
                        </Text>
                    </div>
                    <div style={{ maxHeight: 340, overflowY: "auto", borderRadius: 8, border: `1px solid ${cfg.color}20` }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: cfg.bg, position: "sticky", top: 0, zIndex: 1 }}>
                                    <th style={{ padding: "8px 14px", textAlign: "left", fontSize: 11, color: "#6b7280", fontWeight: 600, width: 36 }}>#</th>
                                    <th style={{ padding: "8px 14px", textAlign: "left", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Prestation</th>
                                    <th style={{ padding: "8px 14px", textAlign: "right", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Montant</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sectionLignes.map((ligne, idx) => (
                                    <tr key={ligne.id || idx} style={{ background: idx % 2 === 0 ? "#fff" : cfg.bg, borderTop: `1px solid ${cfg.color}15` }}>
                                        <td style={{ padding: "9px 14px" }}>
                                            <div style={{
                                                width: 22, height: 22, borderRadius: 5, background: cfg.color,
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                color: "#fff", fontSize: 10, fontWeight: 700,
                                            }}>
                                                {idx + 1}
                                            </div>
                                        </td>
                                        <td style={{ padding: "9px 14px", fontSize: 13 }}>{ligne.nom}</td>
                                        <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600, color: cfg.color, fontSize: 13 }}>
                                            {formatMontant(ligne.montant)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ),
        };
    });

    const getMarchandiseLabel = (id) => marchandises?.find(m => m.id === id)?.nom || "—";

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Space>
                        <Button
                            icon={<FilePdfOutlined />}
                            onClick={async () => await downloadDevisTransit(devis)}
                            style={{
                                borderRadius: 10, background: "linear-gradient(135deg, #22c55e, #16a34a)",
                                border: "none", color: "#fff", fontWeight: 600,
                                boxShadow: "0 4px 12px rgba(34,197,94,0.25)",
                            }}
                        >
                            Exporter EXCEL
                        </Button>
                        <Button onClick={onClose} style={{ borderRadius: 10, background: "#f3f4f6", border: "1px solid #e5e7eb", color: "#374151", fontWeight: 500 }}>
                            Fermer
                        </Button>
                    </Space>
                </div>
            }
            width={620}
            styles={{ body: { padding: "16px 24px 8px" } }}
            title={
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#1e1b4b" }}>
                        Devis Transit
                        <span style={{ marginLeft: 8, fontSize: 13, color: "#6366f1", fontFamily: "monospace" }}>
                            — {devis.devis_ref}
                        </span>
                    </div>
                    <div style={{ fontWeight: 400, fontSize: 8, color: "#8c8c8c", marginTop: 2 }}>
                        {devis.client_nom} · {devis.agence_nom} · {formatDate(devis.devis_date)}
                    </div>
                </div>
            }
        >
            <div style={{
                background: "#f8f9ff", border: "1px solid #e0e7ff", borderRadius: 10,
                padding: "10px 16px", marginBottom: 16, display: "flex", gap: 24, flexWrap: "wrap",
            }}>
                {[
                    { label: "Dossier", value: devis.dossier_ref, mono: true },
                    { label: "Statut", value: devis.dossier_statut },
                    { label: "Créé par", value: devis.created_by },
                    { label: "Type Marchandises", value: getMarchandiseLabel(devis.marchandise_id) },
                    { label: "Nb Container", value: devis.nb_conteneur },
                    { label: "Nb Colis", value: devis.nbColis },
                    { label: "Poids", value: devis.poids_tr },
                    { label: "Volume", value: devis.volume_tr },
                ].map(({ label, value, mono }) => (
                    <div key={label}>
                        <Text type="secondary" style={{ fontSize: 11, display: "block" }}>{label}</Text>
                        <Text strong style={{ fontSize: 8, fontFamily: mono ? "monospace" : undefined }}>{value || "—"}</Text>
                    </div>
                ))}
            </div>
            <Tabs items={tabItems} size="small" style={{ marginBottom: 0 }} />
        </Modal>
    );
};

// ─── Modal changement de statut ───────────────────────────────────────────────
const ChangeStatutModal = ({ open, devis, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [statut, setStatut] = useState(devis?.devis_statut);

    useEffect(() => { setStatut(devis?.devis_statut); }, [devis]);

    const handleSave = async () => {
        if (!statut || statut === devis?.devis_statut) { onClose(); return; }
        setLoading(true);
        try {
            await onSuccess(devis, statut);
            message.success("Statut mis à jour avec succès");
            onClose();
        } catch {
            message.error("Erreur lors de la mise à jour du statut");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            title={
                <Space>
                    <EditOutlined style={{ color: "#6366f1" }} />
                    <span>Changer le statut — <span style={{ fontFamily: "monospace", color: "#6366f1" }}>{devis?.devis_ref}</span></span>
                </Space>
            }
            onCancel={onClose}
            onOk={handleSave}
            confirmLoading={loading}
            okText="Enregistrer"
            cancelText="Annuler"
            okButtonProps={{ style: { background: "#6366f1", borderColor: "#6366f1" } }}
            width={380}
            destroyOnClose
        >
            <div style={{ padding: "12px 0" }}>
                <div style={{ marginBottom: 8, fontSize: 13, color: "#64748b" }}>Nouveau statut</div>
                <Select style={{ width: "100%" }} value={statut} onChange={setStatut}>
                    <Option value="valide_direction">Validé par Masoandro</Option>
                    <Option value="invalide_direction">Invalidé par Masoandro</Option>
                    <Option value="attente_masoandro">En attente de validation Masoandro</Option>
                    <Option value="attente_client">En attente de validation Client</Option>
                    <Option value="valide_client">Validé par le Client</Option>
                    <Option value="invalide_client">Invalidé par le Client</Option>
                </Select>
            </div>
        </Modal>
    );
};

// ─── Composant Principal ──────────────────────────────────────────────────────
const DevisTransit = () => {
    const [listeDevis, setListeDevis] = useState([]);
    const [listeMarchandises, setListeMarchandises] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [viewModal, setViewModal] = useState({ open: false, devis: null });
    const [statutModal, setStatutModal] = useState({ open: false, devis: null });
    const [filters, setFilters] = useState({ search: "", statut: "", agence: "" });

    const fetchDevis = async () => {
        setLoading(true);
        try {
            const res = await getDevisTransit();
            setListeDevis(res.data || []);
        } catch {
            message.error("Erreur lors du chargement des devis");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevis();
        getMMarchandises().then(r => setListeMarchandises(r.data || [])).catch(() => {});
        getAllCategoriesTransit().then(r => setCategories(r.data || [])).catch(() => {});
    }, []);

    const agenceOptions = useMemo(() => {
        const uniq = [...new Set(listeDevis.map(d => d.agence_nom).filter(Boolean))];
        return uniq.map(a => ({ label: a, value: a }));
    }, [listeDevis]);

    const statutOptions = Object.entries(STATUT_CONFIG).map(([k, v]) => ({ label: v.label, value: k }));

    const filteredData = useMemo(() => {
        const q = filters.search.toLowerCase();
        return listeDevis.filter(d => {
            const matchSearch = !q || [d.devis_ref, d.client_nom, d.dossier_ref, d.created_by, d.dossier_statut]
                .some(v => v?.toLowerCase().includes(q));
            const matchStatut = !filters.statut || d.devis_statut === filters.statut;
            const matchAgence = !filters.agence || d.agence_nom === filters.agence;
            return matchSearch && matchStatut && matchAgence;
        });
    }, [listeDevis, filters]);

    const stats = useMemo(() => ({
        total: listeDevis.length,
        attente: listeDevis.filter(d => d.devis_statut === "attente_masoandro").length,
        valide: listeDevis.filter(d => d.devis_statut === "valide_direction" || d.devis_statut === "valide_client").length,
        montantTotal: listeDevis.reduce((s, d) => s + computeTotal(d.devis_infos), 0),
    }), [listeDevis]);

    const clientFilters = useMemo(() =>
        [...new Set(listeDevis.map(d => d.client_nom).filter(Boolean))].map(c => ({ text: c, value: c })),
        [listeDevis]
    );

    const columns = [
        {
            title: "Référence", dataIndex: "devis_ref", key: "devis_ref",
            sorter: (a, b) => a.devis_ref?.localeCompare(b.devis_ref),
            render: (v) => <Text style={{ fontFamily: "monospace", fontSize: 8, color: "#6366f1", fontWeight: 600 }}>{v}</Text>,
        },
        {
            title: "Client", dataIndex: "client_nom", key: "client_nom",
            sorter: (a, b) => a.client_nom?.localeCompare(b.client_nom),
            filters: clientFilters, filterSearch: true,
            onFilter: (value, record) => record.client_nom === value,
            render: (v) => <Text strong style={{ fontSize: 8 }}>{v || "—"}</Text>,
        },
        {
            title: "Dossier", dataIndex: "dossier_ref", key: "dossier_ref",
            render: (v, row) => (
                <div>
                    <Text style={{ fontFamily: "monospace", fontSize: 8 }}>{v}</Text>
                    <Text type="secondary" style={{ fontSize: 11, display: "block" }}>{row.dossier_statut}</Text>
                </div>
            ),
        },
        {
            title: "Agence", dataIndex: "agence_nom", key: "agence_nom",
            sorter: (a, b) => a.agence_nom?.localeCompare(b.agence_nom),
            render: (v) => (
                <Tag icon={<ShopOutlined />} style={{ borderRadius: 6, border: "none", background: "#f0f9ff", color: "#0369a1", fontSize: 11 }}>
                    {v}
                </Tag>
            ),
        },
        {
            title: "Créé par", dataIndex: "created_by", key: "created_by",
            render: (v) => (
                <Space size={6}>
                    <div style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: "linear-gradient(135deg, #6366f1, #818cf8)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>
                        {v?.[0] || "?"}
                    </div>
                    <Text style={{ fontSize: 8 }}>{v || "—"}</Text>
                </Space>
            ),
        },
        {
            title: "Date", dataIndex: "devis_date", key: "devis_date",
            sorter: (a, b) => new Date(a.devis_date) - new Date(b.devis_date),
            render: (v) => <Text style={{ fontSize: 8 }}>{formatDate(v)}</Text>,
        },
        {
            title: "Statut", dataIndex: "devis_statut", key: "devis_statut",
            render: (v) => <StatutBadge statut={v} />,
            filters: statutOptions.map(o => ({ text: o.label, value: o.value })),
            onFilter: (value, record) => record.devis_statut === value,
        },
        {
            title: "Actions", key: "actions", align: "center", width: 80,fixed: "right",
            render: (_, row) => (
                <Space size={4}>
                    <Tooltip title="Voir le devis">
                        <Button shape="circle" icon={<EyeOutlined />} size="small"
                            onClick={(e) => { e.stopPropagation(); setViewModal({ open: true, devis: row }); }}
                            style={{ border: "1px solid #e0e7ff", color: "#6366f1", background: "#f5f3ff" }}
                        />
                    </Tooltip>
                    <Tooltip title="Changer le statut">
                        <Button shape="circle" icon={<EditOutlined />} size="small"
                            onClick={(e) => { e.stopPropagation(); setStatutModal({ open: true, devis: row }); }}
                            style={{ border: "1px solid #d1fae5", color: "#7ca50c", background: "#f0fdf4" }}
                        />
                    </Tooltip>
                  
                </Space>
            ),
        },
    ];

    const handleChangeStatut = async (devis, newStatut) => {
        await toggleDevisTransit({ devis_id: devis.devis_id, statut: newStatut, dossier_id: devis.dossier_id });
        fetchDevis();
    };

    const hasFilters = filters.search || filters.statut || filters.agence;

    return (
        <div style={{
            padding: "20px 24px", background: "#f8f9ff", minHeight: "100vh",
            fontFamily: "'Inter', sans-serif", display: "flex", flexDirection: "column", gap: 14,
        }}>
            {/* ── Header + Stats sur la même ligne ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                {/* Titre */}
                <div style={{ flexShrink: 0 }}>
                    <Space size={8} align="center">
                        <FileTextOutlined style={{ fontSize: 18, color: "#6366f1" }} />
                        <Title level={4} style={{ margin: 0, color: "#1e1b4b", fontWeight: 700 }}>
                            Devis Transit
                        </Title>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 8, display: "block", marginTop: 2 }}>
                        Gestion et suivi des devis de transit
                    </Text>
                </div>

                {/* Séparateur */}
                <div style={{ width: 1, height: 40, background: "#e5e7eb", flexShrink: 0 }} />

                {/* Stats compactes */}
                <div style={{ display: "flex", gap: 10, flex: 1, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 130px", minWidth: 120 }}>
                        <StatCard icon={<FileTextOutlined />} label="Total devis" value={stats.total} color="#6366f1" bgColor="#ede9fe" />
                    </div>
                    <div style={{ flex: "1 1 130px", minWidth: 120 }}>
                        <StatCard icon={<ClockCircleOutlined />} label="En attente" value={stats.attente} color="#f59e0b" bgColor="#fef3c7" />
                    </div>
                    <div style={{ flex: "1 1 130px", minWidth: 120 }}>
                        <StatCard icon={<CheckCircleOutlined />} label="Validés" value={stats.valide} color="#10b981" bgColor="#d1fae5" />
                    </div>
                    <div style={{ flex: "1 1 170px", minWidth: 155 }}>
                        <StatCard icon={<DollarOutlined />} label="Montant total" value={`${stats.montantTotal.toLocaleString("fr-MG")} Ar`} color="#059669" bgColor="#d1fae5" />
                    </div>
                </div>

                {/* Bouton actualiser */}
                <Button
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={fetchDevis}
                    loading={loading}
                    style={{ borderRadius: 8, border: "1px solid #e0e7ff", color: "#6366f1", flexShrink: 0 }}
                >
                    Actualiser
                </Button>
            </div>

            {/* ── Filtres ── */}
            <div style={{
                background: "#fff", borderRadius: 12, padding: "10px 16px",
                border: "1px solid #e5e7eb", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
            }}>
                <Input
                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                    placeholder="Rechercher par réf, client, dossier, créateur…"
                    value={filters.search}
                    onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                    allowClear size="small"
                    style={{ flex: 1, minWidth: 220, borderRadius: 8 }}
                />
                <Select
                    placeholder="Statut" size="small"
                    options={[{ label: "Tous les statuts", value: "" }, ...statutOptions]}
                    value={filters.statut}
                    onChange={v => setFilters(f => ({ ...f, statut: v }))}
                    style={{ width: 190 }} allowClear
                />
                <Select
                    placeholder="Agence" size="small"
                    options={[{ label: "Toutes les agences", value: "" }, ...agenceOptions]}
                    value={filters.agence}
                    onChange={v => setFilters(f => ({ ...f, agence: v }))}
                    style={{ width: 170 }} allowClear
                />
                {hasFilters && (
                    <Button size="small" onClick={() => setFilters({ search: "", statut: "", agence: "" })}
                        style={{ borderRadius: 6, fontSize: 8 }}>
                        Réinitialiser
                    </Button>
                )}
                <Text type="secondary" style={{ fontSize: 8, marginLeft: "auto", whiteSpace: "nowrap" }}>
                    {filteredData.length} résultat{filteredData.length !== 1 ? "s" : ""}
                </Text>
            </div>

            {/* ── Table ── */}
            <Spin spinning={loading} tip="Chargement des devis…">
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
                    <Table
                        dataSource={filteredData}
                        columns={columns}
                        rowKey="devis_id"
                        size="small"
                        scroll={{ x: "max-content" }}
                        pagination={{
                            pageSize: 12,
                            position: ["bottomCenter"],
                            size: "small",
                            style: { marginTop: 8, marginBottom: 8 },
                            itemRender: (page, type, originalElement) => {
                                if (type === "page") {
                                    return (
                                        <div style={{
                                            borderRadius: "50%", minWidth: 28, height: 28,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            border: "1px solid #d9d9d9", fontSize: 8,
                                        }}>
                                            {page}
                                        </div>
                                    );
                                }
                                return originalElement;
                            },
                        }}
                        locale={{ emptyText: <Empty description="Aucun devis trouvé" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 40 }} /> }}
                        onRow={(record) => ({
                            style: { cursor: "pointer" },
                            onClick: () => setViewModal({ open: true, devis: record }),
                        })}
                        rowClassName={(_, idx) => idx % 2 === 0 ? "" : "table-row-alt"}
                        style={{ borderRadius: 0 }}
                    />
                </div>
            </Spin>

            {/* ── Modals ── */}
            <DevisViewModal
                open={viewModal.open}
                onClose={() => setViewModal({ open: false, devis: null })}
                devis={viewModal.devis}
                categories={categories}
                marchandises={listeMarchandises}
            />
            <ChangeStatutModal
                open={statutModal.open}
                devis={statutModal.devis}
                onClose={() => setStatutModal({ open: false, devis: null })}
                onSuccess={handleChangeStatut}
            />

            <style>{`
                .table-row-alt td { background: #fafbff !important; }
                .ant-table-row:hover td { background: #f0f4ff !important; transition: background .15s; }
                .ant-table-thead > tr > th {
                    background: #fafafa !important;
                    font-weight: 600; color: #595959;
                    font-size: 12px; padding: 8px 12px !important;
                }
                .ant-table-tbody > tr > td { padding: 7px 12px !important; }
            `}</style>
        </div>
    );
};

export default DevisTransit;